import _ from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Evisit from "../../libs/Evisit";
import request from "../../libs/request";
import "../../styles/css/base.css";
import "../../styles/css/formio.scss";
import Workflow from "./Workflow";

var workflowTimer = null;

const loadWorkflows = async (roomCode) => {
    let response = null;
    if (roomCode) {
        response = await request.get(`${window.base_url}/ms/rooms/${roomCode}/workflows`);
    } else if (_.get(window, "CONFIG.Account.code")) {
        response = await request.get(`${window.base_url}/ms/accounts/${window.CONFIG.Account.code}/workflows`);
    }
    return response;
};

const loadRoom = async (roomCode) => {
    return await request.get(`${window.base_url}/api_v3/rooms/${roomCode}?extra_fields=currency,livemode`);
}

const loadProviders = async (roomCode) => {
    return await request.get(`${window.base_url}/api_v3/rooms/${roomCode}/providers`);
}

async function loadVisitData (visitId) {
    return await request.get(`${window.base_url}/api_v3/visits/${visitId}`);
}

async function loadPatientData (patientId) {
    return await request.get(`${window.base_url}/api_v3/users/${patientId}`);
}

function isIOSSafari() {
  const ua = navigator.userAgent;
  const regex = /iPad|iPhone|iPod/i; // Check if this is iOS device
  const safari = /Version\/[\d\.]+.*Safari/i; // Check if this is safari

  return regex.test(ua) && safari.test(ua);
}

export function DynamicFlows(props) {
    const [currentWorkflow, setCurrentWorkflow] = useState(false);
    const [workflows, _setWorkflows] = useState(null);
    const workflowsRef = useRef(workflows);
    const setWorkflows = data => {
        workflowsRef.current = data;
        _setWorkflows(data);
    };
    const workflowRef = useRef(null);
    const params = useParams();
    const [components, setComponents] = useState([]);
    const [room, setRoom] = useState(null);
    const [providers, setProviders] = useState([]);
    const [visit, setVisit] = useState(null);
    const [patient, setPatient] = useState(null);
    const query = new URLSearchParams(window.location.search);
    const roomCode = query.get("room_code") ? query.get("room_code") : props.roomCode;
    const visitId = query.get("visit_id") ? query.get("visit_id") : props.visitId;
    const patientId = query.get("patient_id") ? query.get("patient_id") : props.patientId;

    const handleEvents = async (event) => {
        var data = event.data;
        if (!data || !data.name) {
            return;
        }

        clearInterval(workflowTimer);
        if (data.data && data.data.workflow_id) {
            const visit = _.get(data, "data.visit");
            if (typeof visit === "object") { setVisit(visit) }

            if (data.data.visit_id) {
                await loadVisitData(data.data.visit_id).then((res) => { setVisit(res.data) });
            }

            if (data.data.patient_id) {
                await loadPatientData(data.data.patient_id).then((res) => { setPatient(res.data) });
            }
            /*
            if (data.name === "workflow.schedule_appointment") {
                dispatch(setVisit({type: window.CONST.VISIT.TYPE.SCHEDULED}));
            }
            */

            var workflow = _.find(workflowsRef.current, {id: data.data.workflow_id});
            workflow["data"] = data.data;
            setCurrentWorkflow(workflow);

            if (data.name === "workflow.back") {
                const {stack, inCancelFlow, onPrevious, onCancelFlow} = workflowRef.current;
                if (stack.length > 1 || inCancelFlow()) {
                    return onPrevious();
                }

                onCancelFlow();
            }
        }
    };

    const handleFocusOutEvents = (event) => {
        if (!isIOSSafari()) {
            // This function is only for iOS safari
            // iOS safari can't handle overflow:hidden well.
            // As a result, when the intake modal shows, the parent page can still scroll
            // The workaround is that when the input field lose focus, the iframe pass an
            // event to the parent page to scroll to the top.
            return;
        }
        var inputFields = ['INPUT', 'TEXTAREA'];
        if (inputFields.indexOf(event.srcElement?.nodeName) > -1
            && inputFields.indexOf(event.relatedTarget?.nodeName) === -1) {
            Evisit.postMessageBackToParent({
                name: "ios.focusout"
            });
        }
    }

    useEffect(() => {
        if (roomCode) {
            loadRoom(roomCode).then((res) => {
                setRoom(res["data"]);

            });
            loadProviders(roomCode).then((res) => {
                setProviders(res["data"]);
            });
        }

    }, [roomCode]);

    useEffect(() => {
        loadWorkflows(roomCode).then((res) => {
            if (res) {
                let workflowsData = _.get(res, "data.workflows", []);
                setWorkflows(workflowsData);
                setComponents(_.get(res, "data.components", []));
                const workflowId = params.workflow_id || props.workflow_id;
                if (workflowId) {
                    const workflow = _.find(workflowsData, {id: workflowId});
                    setCurrentWorkflow(workflow);
                }

                workflowTimer = setInterval(() => {
                    // When the message is posted, parent may not be ready yet. So we may need to post multiple times
                    Evisit.postMessageBackToParent({
                        name: "workflow.ready"
                    });
                }, 1000);
            }
        });

        window.addEventListener('message', handleEvents);
        window.addEventListener('focusout', handleFocusOutEvents);
        return () => {
            window.removeEventListener('message', handleEvents);
            window.removeEventListener('focusout', handleFocusOutEvents);
        };
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        params.workflow_id, 
        roomCode,
        props.workflow_id,
    ]);

    useEffect(() => {
        if (!currentWorkflow || !components.length || !workflowRef.current) {
            return;
        }
        workflowRef.current?.start(currentWorkflow, components);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWorkflow, workflows, components, workflowRef]);

    useEffect(() => {
        if (visitId) {
            loadVisitData(visitId).then((res) => { setVisit(res.data) });
        }
    }, [visitId]);
    
    useEffect(() => {
        if (patientId) {
            loadPatientData(patientId).then((res) => { setPatient(res.data) });
        }
    }, [patientId]);

    const onWorkflowEnd = () => {
        setCurrentWorkflow(false);
    }

    const generateWorkflow = () => {
        if (!currentWorkflow) {
            return null;
        }
        var workflowProps = {
            ref: workflowRef,
            roomCode: roomCode
        };
        if (room) {
            workflowProps["room"] = room;
        }
        if (providers) {
            workflowProps["providers"] = providers;
        }
        if (visit) {
            workflowProps["visit"] = visit;
        }
        
        if (patient) {
            workflowProps["patient"] = patient;
        }
        workflowProps["onEnd"] = onWorkflowEnd;

        return <Workflow {...workflowProps} />;
    }

    return generateWorkflow();
}

